/**
 * @NOTE - Types in these files should map to the equivalent types in our `mem-client-desktop` project.
 */

import type { UploadableAttachment, SkippedAttachment } from "@/modules/file-import/upload/types";
import type {
  AiToolkitToolInput,
  AiToolkitToolName,
  AiToolkitToolOutput,
  AiToolkitSerializedError,
} from "@/modules/ai-toolkit";

/**
 * Matches a subset of the events defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 *
 * Note that `realtime-event` can match a variety of events which are "registered" by
 * our backend.
 *
 * For more info, see the `recall client` in our python backend code.
 */
export enum RecallEventKind {
  RealtimeEvent = "realtime-event",
  MeetingDetected = "meeting-detected",
  UploadProgress = "upload-progress",
  RecordingEnded = "recording-ended",
}

/**
 * Matches a subset of the events defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export type RecallEventWindow = {
  id: string;
  title?: string;
  url?: string;
  platform: string;
};

/**
 * Matches a subset of the events defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export enum RecallMeetingDetectedEventKind {
  MeetingDetected = "meeting-detected",
  MeetingClosed = "meeting-closed",
}

/**
 * Matches the permission kinds defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export enum RecallPermissionKind {
  Accessibility = "accessibility",
  ScreenCapture = "screen-capture",
  SystemAudio = "system-audio",
  Microphone = "microphone",
  FullDiskAccess = "full-disk-access",
}

/**
 * Matches the statuses defined in the Recall.ai Desktop SDK.
 * - https://docs.recall.ai/docs/desktop-sdk
 */
export enum RecallPermissionStatus {
  Granted = "granted",
  NotRequested = "not_requested",
  Denied = "denied",
}

export enum RecallSdkState {
  Idle = "idle",
  Initializing = "initializing",
  Running = "running",
  Crashed = "crashed",
}

export enum RecallSdkErrorKind {
  SdkError = "sdk_error",
  StatusCode = "status_code",
  ProcessExit = "process_exit",
  OsError = "os_error",
}

export type RecallSdkErrorDiagnostics = {
  recallSdkErrorKind?: RecallSdkErrorKind;
  recallSdkEventKind?: string;
  recallSdkErrorType?: string;
  recallSdkErrorName?: string;
  recallSdkErrorCode?: string | number;
  recallSdkStatusCode?: number;
  recallSdkErrorErrno?: number;
  recallSdkErrorSyscall?: string;
  recallSdkNormalizedErrorMessage?: string;
  recallSdkShutdownCode?: number;
  recallSdkShutdownCodeHex?: string;
  recallSdkShutdownSignal?: string | null;
  recallSdkShutdownIntentional?: boolean;
  recallSdkProcessExitCode?: number;
  recallSdkProcessExitCodeHex?: string;
  recallSdkProcessExitSignal?: string | null;
  recallSdkOsErrorCode?: number;
  recallSdkOsErrorCodeHex?: string;
  recallSdkOsErrorKind?: string;
  recallSdkOsErrorMessage?: string;
};

export type RecallSdkStatePayload = {
  state: RecallSdkState;
  errorMessage?: string;
  errorDiagnostics?: RecallSdkErrorDiagnostics;
};

export type RecallPermissionMap = Partial<Record<RecallPermissionKind, RecallPermissionStatus>>;

export interface CalendarEvent {
  id: string;
  calendarEventId: string;
  title?: string;
  url?: string;
  platform: string;
  startTime?: string | null;
  endTime?: string | null;
  attendees?: string[] | null;
}

export type ElectronAppInfo = {
  electronSystemArchitecture: string;
  electronAppVersion: string;
  electronRuntimeVersion: string;
};

export type DesktopAudioCaptureDiagnosticsContext = {
  recallSdkStateAtStart?: RecallSdkState;
  recallPermissionsAtStart?: RecallPermissionMap;
};

export type DesktopAudioArtifactMetadata = Record<string, unknown> & {
  audioRecordingId?: string;
  sampleRate?: number;
} & DesktopAudioCaptureDiagnosticsContext;

export type DesktopAudioRecordingRef = {
  audioRecordingId: string;
};

export enum DesktopAudioArtifactRemoveReason {
  SessionInitializationFailed = "session_initialization_failed",
  SessionAborted = "session_aborted",
  FinalizeFailed = "finalize_failed",
  LocalArtifactsCleared = "local_artifacts_cleared",
  ExplicitRemoval = "explicit_removal",
}

export type DesktopRemoveAudioArtifactArgs = {
  audioRecordingId: string;
  removeReason?: DesktopAudioArtifactRemoveReason;
};

export enum DesktopAudioRecordingStrategy {
  WebArtifact = "web-artifact",
  DesktopArtifact = "desktop-artifact",
}

export type DesktopRecordingStartAttemptTrace = {
  rootStartAttemptId: string;
  startAttemptId: string;
  attemptNumber: number;
};

export enum DesktopRecordingStartFailureSource {
  RendererPrepare = "renderer_prepare",
  RendererInitializationTimeout = "renderer_initialization_timeout",
  RecallCoordinatorStart = "recall_coordinator_start",
  RecallCoordinatorRestart = "recall_coordinator_restart",
}

export enum DesktopRecordingStartFailurePhase {
  PrepareWindow = "prepare_window",
  UploadCredentials = "upload_credentials",
  InitializeAndStart = "initialize_and_start",
  RecallStart = "recall_start",
  RecallRestart = "recall_restart",
}

// Keep in sync with identical definitions in:
//   - mem-client-desktop/preload/types.ts (preload compilation unit)
//   - mem-client-desktop/src/modules/recording-coordinator/types.ts (main process)
export enum StartRecordingOutcomeKind {
  Started = "started",
  RestartRequired = "restart_required",
  Fatal = "fatal",
}

export type StartRecordingOutcome =
  | { outcome: StartRecordingOutcomeKind.Started }
  | {
      outcome: StartRecordingOutcomeKind.RestartRequired;
      reason: string;
      errorDiagnostics?: RecallSdkErrorDiagnostics;
    }
  | {
      outcome: StartRecordingOutcomeKind.Fatal;
      error: string;
      errorDiagnostics?: RecallSdkErrorDiagnostics;
    };

export type DesktopAudioArtifactRef = {
  artifactId: string;
  key: string;
  mimeType: string;
  sizeBytes: number;
  durationMs?: number;
  appendCount: number;
  bytesWritten: number;
  createdAt: string;
  updatedAt: string;
  firstAppendAt?: string;
  lastAppendAt?: string;
  finalizedAt?: string;
  metadata?: DesktopAudioArtifactMetadata;
  filePath: string;
};

export enum DesktopAudioArtifactState {
  Writing = "writing",
  Finalized = "finalized",
}

export enum DesktopAudioArtifactFailureReason {
  MissingManifest = "missing_manifest",
  MissingArtifactFile = "missing_artifact_file",
  NotFinalized = "not_finalized",
  EmptyFile = "empty_file",
  LookupFailed = "lookup_failed",
}

export type DesktopAudioArtifactSnapshot = DesktopAudioArtifactRef & {
  state: DesktopAudioArtifactState;
};

export type DesktopAudioArtifactUploadFailureDebugInfo = {
  reason: DesktopAudioArtifactFailureReason;
  artifactDirectoryPath: string;
  manifestPath: string;
  artifactFilePath: string;
  artifactState?: DesktopAudioArtifactState;
  artifactSizeBytes?: number;
  appendCount?: number;
  appendFailureCount?: number;
  bytesWritten?: number;
  createdAt?: string;
  updatedAt?: string;
  firstAppendAt?: string;
  lastAppendAt?: string;
  finalizedAt?: string;
  artifactRefPresent: boolean;
  artifactSnapshotPresent: boolean;
  removeReason?: DesktopAudioArtifactRemoveReason;
  removedAt?: string;
  diagnosticErrorMessage?: string;
};

export type DesktopAudioUploadMeta = {
  capturedAudioId: string;
  associatedAudioRecordingId: string;
  associatedNoteId?: string | null;
  associatedSessionId?: string | null;
  transcriptText: string;
  transcriptArray: unknown[];
  preferredMimeType?: string;
};

export type OAuthAccessTokenSetArgs = {
  token: string;
};

export type OAuthAccessTokenGetRequestArgs = {
  requestId: string;
};

export type OAuthAccessTokenGetResponseArgs = {
  requestId: string;
  token: string | null;
};

export type AiToolkitRequestArgs = {
  requestId: string;
  toolName: AiToolkitToolName;
  input: AiToolkitToolInput;
};

export type AiToolkitResponseArgs = {
  requestId: string;
} & (
  | {
      ok: true;
      output: AiToolkitToolOutput;
    }
  | {
      ok: false;
      error: AiToolkitSerializedError;
    }
);

export enum WorkingContextSubscriptionSource {
  Agent = "agent",
  App = "app",
  Debug = "debug",
}

export enum WorkingContextObservationSource {
  BrowserExtension = "browser-extension",
  ForegroundWindow = "foreground-window",
  ScreenOcr = "screen-ocr",
  Accessibility = "accessibility",
  AppIntegration = "app-integration",
  Agent = "agent",
  Unknown = "unknown",
}

export enum WorkingContextMatchKind {
  ObservationMatchedSubscription = "observation-matched-subscription",
}

export enum WorkingContextSuppressionReason {
  NoActiveSubscriptions = "no-active-subscriptions",
  NoSubscriptionTerms = "no-subscription-terms",
  BelowThreshold = "below-threshold",
}

export type WorkingContextSubscription = {
  id: string;
  source: WorkingContextSubscriptionSource;
  createdAt: string;
  query: string;
  expiresAt?: string;
  metadata?: Record<string, unknown>;
};

export type WorkingContextObservationRawRef = {
  kind: string;
  id: string;
};

export type WorkingContextObservation = {
  id: string;
  source: WorkingContextObservationSource;
  observedAt: string;
  appName?: string;
  windowTitle?: string;
  title?: string;
  url?: string;
  text?: string;
  selectedText?: string;
  rawRef?: WorkingContextObservationRawRef;
  metadata?: Record<string, unknown>;
};

export type WorkingContextObservationSummary = {
  id: string;
  source: WorkingContextObservationSource;
  observedAt: string;
  appName?: string;
  title?: string;
  windowTitle?: string;
  url?: string;
  previewText?: string;
  hasPrivateRawRef: boolean;
};

export type WorkingContextMatchDecision = {
  subscriptionId: string;
  observationId: string;
  score: number;
  matchedTerms: string[];
  suppressedReason?: WorkingContextSuppressionReason;
};

export type WorkingContextMatch = {
  id: string;
  kind: WorkingContextMatchKind;
  createdAt: string;
  subscriptionId: string;
  observation: WorkingContextObservationSummary;
  score: number;
  matchedTerms: string[];
  reason: string;
};

export type WorkingContextFocusState = {
  observation: WorkingContextObservationSummary;
  updatedAt: string;
};

export type WorkingContextStats = {
  activeSubscriptionCount: number;
  recentObservationCount: number;
  recentMatchCount: number;
  observationCapacity: number;
  matchCapacity: number;
};

export type WorkingContextSnapshot = {
  generatedAt: string;
  activeSubscriptions: WorkingContextSubscription[];
  focus: WorkingContextFocusState | null;
  recentObservations: WorkingContextObservationSummary[];
  recentMatches: WorkingContextMatch[];
  stats: WorkingContextStats;
};

export type WorkingContextStatus = WorkingContextStats & {
  running: boolean;
};

export type WorkingContextAddSubscriptionArgs = {
  subscriptionId: string;
  query: string;
};

export type WorkingContextRemoveSubscriptionArgs = {
  subscriptionId: string;
};

export type StartDesktopAudioUploadArgs = {
  artifactRef: DesktopAudioArtifactRef;
  uploadMeta: DesktopAudioUploadMeta;
  authToken: string;
  forceRestart?: boolean;
};

export type StartDesktopAudioUploadResult = { success: true } | { success: false; error: string };

export type DesktopAudioUploadProgressEvent = {
  audioRecordingId: string;
  progressPercent: number;
};

export type DesktopAudioUploadCompletedEvent = {
  audioRecordingId: string;
};

export type DesktopAudioUploadErrorEvent = {
  audioRecordingId: string;
  errorMessage: string;
};

export type DesktopAudioWaveformUpdateEvent = {
  audioRecordingId: string;
  update: {
    kind: "normalized";
    sampleRate: number;
    samples: Float32Array;
    durationMs: number;
  };
};

export enum DesktopRecordingSessionState {
  Recording = "recording",
  Paused = "paused",
}

export type DesktopRecordingSession = {
  audioRecordingId: string;
  recordingStrategy: DesktopAudioRecordingStrategy;
  state: DesktopRecordingSessionState;
};

export enum DesktopRecordingRecoveryKind {
  LiveSession = "live-session",
  UnfinishedArtifact = "unfinished-artifact",
}

export type DesktopRecordingRecoveryState =
  | {
      kind: DesktopRecordingRecoveryKind.LiveSession;
      recordingStrategy: DesktopAudioRecordingStrategy;
      sessionState: DesktopRecordingSessionState;
      durationMs: number | null;
    }
  | {
      kind: DesktopRecordingRecoveryKind.UnfinishedArtifact;
      recordingStrategy: DesktopAudioRecordingStrategy.DesktopArtifact;
      durationMs: number;
    };

/**
 * Result from importing Apple Notes via IPC
 */
export type ImportAppleNotesResult = {
  success: boolean;
  error?: string;
  notes: ImportedAppleNote[];
  notesPerTag: Record<string, number>;
};

/**
 * An imported Apple Note from the IPC channel
 */
export type ImportedAppleNote = {
  id: number;
  title: string;
  folder: string;
  folderPath: string;
  account: string;
  createdAt: string;
  modifiedAt: string;
  isPinned: boolean;
  tags: string[];
  content: string;
  /** Attachments ready for upload (base64-encoded) */
  attachments: UploadableAttachment[];
  /** Attachments that were skipped (e.g., due to size limits) */
  skippedAttachments?: SkippedAttachment[];
};

/**
 * Result from fetching a single note's content with attachments
 */
export type GetAppleNoteContentResult = {
  content: string;
  attachments: UploadableAttachment[];
  skippedAttachments: SkippedAttachment[];
};

export enum MenuActionEvent {
  CREATE_NOTE = "create-note",
  OPEN_SETTINGS = "open-settings",
}

export type DesktopShortcutEvent = {
  altKey: boolean;
  code: string;
  ctrlKey: boolean;
  key: string;
  metaKey: boolean;
  shiftKey: boolean;
};
